/** Canvas-level error boundary — prevents a single render failure from blanking the page. */

import { Button, Result } from 'antd'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { withTranslation, type WithTranslation } from 'react-i18next'

interface Props extends WithTranslation {
  children: ReactNode
}

interface State {
  errorId: string | null
}

function makeErrorId(): string {
  return `canvas-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

class CanvasErrorBoundary extends Component<Props, State> {
  state: State = { errorId: null }

  static getDerivedStateFromError(): State {
    return { errorId: makeErrorId() }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[CanvasErrorBoundary]', error, info.componentStack)
  }

  private handleReload = () => {
    window.location.reload()
  }

  render() {
    const { t } = this.props
    if (this.state.errorId) {
      return (
        <div className="pcv-error-boundary">
          <Result
            status="error"
            title={t('errorBoundary.title')}
            subTitle={t('errorBoundary.errorId', { id: this.state.errorId })}
            extra={
              <Button type="primary" onClick={this.handleReload}>
                {t('common.reload')}
              </Button>
            }
          />
        </div>
      )
    }
    return this.props.children
  }
}

export default withTranslation('canvas')(CanvasErrorBoundary)
